import { z } from "zod";

import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from "../constants/password.js";

/**
 * The signed-in user's own account (self domain): a lean identity view,
 * deliberately narrower than `UserResponse` (which is the admin user-management
 * DTO with role / licence / subscription fields). Snake_case, consumed directly
 * by the front.
 */
export const AccountSchema = z.object({
    id: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.email(),
});
export type Account = z.infer<typeof AccountSchema>;

/**
 * Body of `PATCH /account`: any subset of the identity fields.
 *
 * `email` is deliberately ABSENT. The sign-in email is a credential, not an
 * identity field: moving it takes a re-authenticated request and a link followed
 * at the new address (`POST /account/email` → `POST /auth/confirm-email-change`).
 * Leaving it out here rather than guarding it is the whole point: Fastify strips
 * the undeclared field, so there is no unverified path left to forget. See PI-34.
 */
export const UpdateAccountRequestSchema = z.object({
    first_name: z.string().min(1).optional(),
    last_name: z.string().min(1).optional(),
});
export type UpdateAccountRequest = z.infer<typeof UpdateAccountRequestSchema>;

export const AccountResponseSchema = z.object({
    success: z.boolean(),
    data: AccountSchema,
});
export type AccountResponse = z.infer<typeof AccountResponseSchema>;

/**
 * Body of `POST /account/password`. The current password re-authenticates the
 * caller, so a stolen session alone cannot take the account over. Length bounds
 * mirror the backend domain policy (both read `shared/constants/password`); the
 * remaining rules (breached-password screening, "must differ from the current
 * one") need server-side knowledge and are enforced by the use-case.
 */
export const ChangePasswordRequestSchema = z.object({
    current_password: z.string().min(1),
    new_password: z.string().min(MIN_PASSWORD_LENGTH).max(MAX_PASSWORD_LENGTH),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

export const ChangePasswordResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        ok: z.boolean(),
        /** How many OTHER sessions were signed out, so the UI can say so. */
        revoked_sessions: z.number(),
    }),
});
export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>;

/**
 * Body of `POST /account/email`: request a change of sign-in email.
 *
 * `current_password` re-authenticates the caller, exactly as it does for
 * `POST /account/password` and for the same reason: a stolen session alone must
 * not be enough to move the login identifier. That challenge is only half the
 * flow: the address supplied here becomes PENDING and takes effect only once the
 * link mailed to it is followed.
 */
export const RequestEmailChangeRequestSchema = z.object({
    current_password: z.string().min(1),
    new_email: z.email(),
});
export type RequestEmailChangeRequest = z.infer<typeof RequestEmailChangeRequestSchema>;

/** A change of sign-in email awaiting confirmation at `pending_email`. */
export const PendingEmailChangeSchema = z.object({
    pending_email: z.email(),
    expires_at: z.string(),
});
export type PendingEmailChange = z.infer<typeof PendingEmailChangeSchema>;

export const RequestEmailChangeResponseSchema = z.object({
    success: z.boolean(),
    data: PendingEmailChangeSchema,
});
export type RequestEmailChangeResponse = z.infer<typeof RequestEmailChangeResponseSchema>;

/**
 * `GET /account/email`: the pending change, or `null` when there is none. Read
 * by the account screen so a reload still shows what is in flight, and so the
 * owner of a hijacked session can SEE a request they did not make.
 *
 * The nullable value is WRAPPED in `{ pending }` rather than being `data` itself,
 * and that is not cosmetic. The front's api client unwraps with
 * `body.data ?? body` (`src/shared/api/api-client.ts`), so a literal `data: null`
 * falls through the `??` and hands the caller the whole `{ success, data }`
 * envelope: a truthy object that every `if (!result)` guard sails past. Keeping
 * `data` a non-null object makes "nothing pending" expressible.
 */
export const PendingEmailChangeResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        pending: PendingEmailChangeSchema.nullable(),
    }),
});
export type PendingEmailChangeResponse = z.infer<typeof PendingEmailChangeResponseSchema>;

/** `DELETE /account/email`: `cancelled` is false when there was nothing pending. */
export const CancelEmailChangeResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        ok: z.boolean(),
        cancelled: z.boolean(),
    }),
});
export type CancelEmailChangeResponse = z.infer<typeof CancelEmailChangeResponseSchema>;

/**
 * Body of `POST /auth/confirm-email-change`: commit a pending change from the
 * token in the emailed link. Public (the caller need not, and after the commit
 * cannot, hold a session for that account), so the token is the whole authority.
 */
export const ConfirmEmailChangeRequestSchema = z.object({
    token: z.string().min(1),
});
export type ConfirmEmailChangeRequest = z.infer<typeof ConfirmEmailChangeRequestSchema>;

export const ConfirmEmailChangeResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        ok: z.boolean(),
        /** The address that is now the sign-in identifier, so the page can name it. */
        email: z.email(),
        /** Sessions signed out by the commit: every one of them, by design. */
        revoked_sessions: z.number(),
    }),
});
export type ConfirmEmailChangeResponse = z.infer<typeof ConfirmEmailChangeResponseSchema>;

/**
 * The signed-in user's LinkedIn cookies: a per-user secret for the enrichment
 * engine, owned as an account self-resource (`/account/linkedin-cookies`).
 * Values are decrypted on the wire; encrypted at rest by the backend adapter.
 */
export const LinkedinCookiesSchema = z.object({
    li_at: z.string(),
    jsession_id: z.string(),
    cookies_updated_at: z.string().nullable(),
});
export type LinkedinCookies = z.infer<typeof LinkedinCookiesSchema>;

/** Body of `PATCH /account/linkedin-cookies`: both values required. */
export const UpdateLinkedinCookiesRequestSchema = z.object({
    li_at: z.string(),
    jsession_id: z.string(),
});
export type UpdateLinkedinCookiesRequest = z.infer<typeof UpdateLinkedinCookiesRequestSchema>;

export const LinkedinCookiesResponseSchema = z.object({
    success: z.boolean(),
    data: LinkedinCookiesSchema,
});
export type LinkedinCookiesResponse = z.infer<typeof LinkedinCookiesResponseSchema>;

/**
 * Response of `POST /account/avatar` and `DELETE /account/avatar` (#579): the
 * account's profile photo after the change: its public URL, or null once removed.
 * The upload itself is multipart (one file), so it has no JSON body schema.
 */
export const AvatarResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        avatar_url: z.string().nullable(),
    }),
});
export type AvatarResponse = z.infer<typeof AvatarResponseSchema>;
